import { clsx } from 'clsx';
import type { KeyboardEventHandler } from 'react';

import { getColorCSSVar } from '../../utils/colors';
import { toCSSSize } from '../../utils/css';
import { fakeButtonProps, handleEnterKey } from '../../utils/handleEnterKey';
import { marginCSSVars } from '../../utils/spacing';
import type { IconProps, IconSVGProps } from './icon.types';

import styles from './icon.module.css';

/**
 * Reefer offers a set of beautiful icons for use across application.
 */
export function Icon({
  altText,
  className,
  color,
  contrastCompliant = false,
  height,
  icon: Icon,
  id,
  m,
  mb,
  ml,
  mr,
  mt,
  mx,
  my,
  onClick,
  rotate = 'up',
  size = 'md',
  'data-testid': testId,
  style,
  title,
  width,
  ...props
}: IconProps) {
  // Accessibility check
  if (onClick && !altText) {
    throw Error(
      `[reefer/icon]: Icons with onClick functionality should provide an "altText" prop.`
    );
  }

  const iconProps: IconSVGProps = {
    'aria-hidden': !altText,
    'aria-label': altText,
    className: clsx(
      styles['icon'],
      rotate !== 'up' && styles[`rotate-${rotate}`],
      {
        [styles['icon-fill-color']]: !!color,
        [styles['icon-stroke-color']]: !!color,
        [styles['icon-contrast-compliant']]: contrastCompliant,
      }
    ),
    rotate,
    color: color || undefined,
    id,
    role: 'img',
  };

  const iconStrokeColor = color ? getColorCSSVar(color) : 'currentColor';

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      data-testid={testId}
      className={clsx(className, styles['icon_container'], {
        [styles['icon_container__pointer']]: !!onClick,
      })}
      title={title}
      onClick={onClick}
      onKeyUp={(event) =>
        handleEnterKey(event, onClick as unknown as KeyboardEventHandler)
      }
      {...(onClick && fakeButtonProps)}
      {...props}
      style={{
        '--icon-fill-color':
          iconStrokeColor !== 'currentColor' ? iconStrokeColor : undefined,
        '--icon-stroke-color':
          iconStrokeColor !== 'currentColor' ? iconStrokeColor : undefined,
        ...marginCSSVars({ m, mb, ml, mr, mt, mx, my }),
        '--icon-height': height
          ? toCSSSize(height)
          : `var(--icon-sizes-${size})`,
        '--icon-width': width ? toCSSSize(width) : `var(--icon-sizes-${size})`,
        ...style,
      }}
    >
      <Icon {...iconProps} />
    </div>
  );
}
